import { bindPurchaseOwner, clearPurchaseIntent } from '@/components/DmpV2/purchaseIntent';
import { clearCheckouts } from '@/components/DmpV2/checkoutState';
import { clearApplications } from '@/components/DmpV2/applicationState';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../store';
import { disconnect } from '../notification/notification.slice';
import { getAuthRefreshDeadline } from '@/utils/authToken';

type AuthState = {
  isLogin: boolean;
  accessToken?: string;
  refreshTokenTime?: number;
  userData: MemberProfile | any;
  applications?: UserApplicationContext;
  activeApplication?: ApplicationKey | null;
  registDone: boolean;
};

const INITAIL_STATE: AuthState = {
  isLogin: false,
  accessToken: '',
  userData: {},
  applications: undefined,
  activeApplication: null,
  registDone: true,
  refreshTokenTime: getAuthRefreshDeadline(),
};

const ACTIVE_APPLICATION_STORAGE_KEY = 'ebram.activeApplication';

const getStoredActiveApplication = (): ApplicationKey | null => {
  if (typeof window === 'undefined') return null;

  const value = window.localStorage.getItem(ACTIVE_APPLICATION_STORAGE_KEY);
  return value === 'dmp' || value === 'free_legal_link' || value === 'legal_cloud' ? value : null;
};

const persistActiveApplication = (application: ApplicationKey | null | undefined) => {
  if (typeof window === 'undefined') return;

  if (!application) {
    window.localStorage.removeItem(ACTIVE_APPLICATION_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(ACTIVE_APPLICATION_STORAGE_KEY, application);
};

const resolveActiveApplication = (state: AuthState): ApplicationKey | null => {
  const available = state.applications?.available || [];
  const requested = state.activeApplication || getStoredActiveApplication();

  if (requested && available.some((application) => application.key === requested)) {
    return requested;
  }

  if (available.length === 1) {
    return available[0].key;
  }

  return null;
};

const authSlice = createSlice({
  name: 'auth',
  initialState: INITAIL_STATE,
  reducers: {
    signIn: (state, action: PayloadAction<AuthState>) => {
      const previousOwner = String(state.userData?.id || '');
      const nextOwner = String(action.payload.userData?.id || '');
      if (previousOwner && nextOwner && previousOwner !== nextOwner) {
        clearPurchaseIntent(true);
        clearCheckouts();
        clearApplications();
      }
      if (nextOwner && action.payload.isLogin) bindPurchaseOwner(nextOwner);
      const nextPayload = { ...action.payload, activeApplication: resolveActiveApplication(action.payload) };
      persistActiveApplication(nextPayload.activeApplication);

      if (nextPayload.userData.main_account_company_info) {
        if (nextPayload.userData.main_account_company_info.isMainAccount) {
          return nextPayload;
        } else {
          const { mainAccountData } = nextPayload.userData.main_account_company_info;
          if (!mainAccountData && typeof mainAccountData === 'string') return nextPayload;
          const companyInfo = {
            acc_coname: mainAccountData?.acc_coname,
            acc_logo: mainAccountData?.acc_logo ?? '/images/default_company_logo.jpg',
            acc_address: mainAccountData?.acc_address,
            acc_desc: mainAccountData?.acc_desc,
            com_email: mainAccountData?.com_email,
            acc_tel: mainAccountData?.acc_tel,
            acc_fax: mainAccountData?.acc_fax,
            acc_website: mainAccountData?.acc_website,
            acc_category: mainAccountData?.acc_category,
            acc_languages: mainAccountData?.acc_languages,
            acc_currency: mainAccountData?.acc_currency,
            acc_countryCode: mainAccountData?.acc_countryCode,
            acc_category_other: mainAccountData?.acc_category_other,
            acc_languages_other: mainAccountData?.acc_languages_other,
            acc_currency_other: mainAccountData?.acc_currency_other,
          };
          return { ...nextPayload, userData: { ...nextPayload.userData, ...companyInfo } };
        }
      } else {
        return nextPayload;
      }
    },
    signOut: () => {
      clearPurchaseIntent(true);
      clearCheckouts();
      clearApplications();
      disconnect();
      persistActiveApplication(null);
      return INITAIL_STATE;
    },
    setActiveApplication: (state, action: PayloadAction<ApplicationKey | null>) => {
      state.activeApplication = action.payload;
      persistActiveApplication(action.payload);
    },
    updateApplications: (state, action: PayloadAction<UserApplicationContext | undefined>) => {
      state.applications = action.payload;
      state.activeApplication = resolveActiveApplication(state);
      persistActiveApplication(state.activeApplication);
    },
    update: (state, action: PayloadAction<AuthState>) => {
      state.userData = action.payload.userData;
      state.registDone = action.payload.registDone;
    },
    updateRegistDone: (state, action: PayloadAction<boolean>) => {
      state.registDone = action.payload;
    },
    unreadCounterChange: (state, action: PayloadAction<number>) => {
      state.userData.unread_message_count = action.payload;
    },
    addUnreadCounter: (state) => {
      state.userData.unread_message_count++;
    },
    updateLang: (state, action: PayloadAction<string>) => {
      state.userData.lang = action.payload;
    },
    renewUserData: (state, action: PayloadAction<MemberProfile>) => {
      state.userData = action.payload;
    },
    switchLayout: (state, action: PayloadAction<boolean>) => {
      state.isLogin = action.payload;
    },
  },
});

export const {
  signIn,
  signOut,
  setActiveApplication,
  updateApplications,
  update,
  updateRegistDone,
  unreadCounterChange,
  addUnreadCounter,
  updateLang,
  renewUserData,
  switchLayout,
} = authSlice.actions;

export const getAuthSelector = (store: RootState) => store.auth;

export default authSlice.reducer;
